import React from "react";
import { Check, Trash2, MessageSquare, UserPlus, AlertCircle } from "lucide-react";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import { Notification } from "../../hooks/useNotifications";

// Helper function to format time ago
const formatTimeAgo = (timestamp: number): string => {
  const now = Date.now();
  const diff = now - timestamp;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  if (seconds < 60) return "just now";
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? "s" : ""} ago`;
  if (hours < 24) return `${hours} hour${hours !== 1 ? "s" : ""} ago`;
  if (days < 7) return `${days} day${days !== 1 ? "s" : ""} ago`;
  if (weeks < 4) return `${weeks} week${weeks !== 1 ? "s" : ""} ago`;
  if (months < 12) return `${months} month${months !== 1 ? "s" : ""} ago`;
  return `${years} year${years !== 1 ? "s" : ""} ago`;
};

interface NotificationItemProps {
  notification: Notification;
  onClick: () => void;
  onMarkAsRead: () => void;
  onDelete: () => void;
}

const getNotificationIcon = (type: string) => {
  switch (type) {
    case "MENTORING_REQUEST":
      return <UserPlus className="h-5 w-5 text-primary" />;
    case "MENTOR_ACCEPTED":
    case "MENTOR_JOINED":
      return <UserPlus className="h-5 w-5 text-green-500" />;
    case "NEW_MESSAGE":
      return <MessageSquare className="h-5 w-5 text-purple-500" />;
    case "ENGAGEMENT_UPDATE":
    case "ENGAGEMENT_COMPLETED":
      return <Check className="h-5 w-5 text-green-500" />;
    case "SYSTEM_REMINDER":
    case "SYSTEM_ANNOUNCEMENT":
      return <AlertCircle className="h-5 w-5 text-orange-500" />;
    default:
      return <AlertCircle className="h-5 w-5 text-gray-500" />;
  }
};

export const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onClick,
  onMarkAsRead,
  onDelete,
}) => {
  const [showActions, setShowActions] = React.useState(false);

  const handleMarkAsRead = (e: React.MouseEvent) => {
    e.stopPropagation();
    onMarkAsRead();
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete();
  };

  const timeAgo = formatTimeAgo(notification.createdAt);

  return (
    <div
      className={cn(
        "relative p-3 md:p-4 hover:bg-accent cursor-pointer transition-colors",
        !notification.isRead && "bg-accent/50"
      )}
      onClick={onClick}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      <div className="flex gap-2 md:gap-3">
        {/* Icon */}
        <div className="flex-shrink-0 mt-0.5">
          {getNotificationIcon(notification.type)}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h4
                  className={cn(
                    "text-sm font-medium truncate",
                    !notification.isRead && "font-semibold"
                  )}
                >
                  {notification.title}
                </h4>
                {!notification.isRead && (
                  <div className="flex-shrink-0 h-2 w-2 rounded-full bg-primary" />
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-0.5 md:mt-1 line-clamp-2">
                {notification.message}
              </p>
              {notification.metadata?.fromUser && (
                <p className="text-xs text-muted-foreground mt-0.5 md:mt-1">
                  From: {notification.metadata.fromUser.displayName}
                </p>
              )}
              <p className="text-xs text-muted-foreground mt-0.5 md:mt-1">{timeAgo}</p>
            </div>

            {/* Actions */}
            {showActions && (
              <div className="flex items-center gap-1 flex-shrink-0">
                {!notification.isRead && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 active:scale-95 active:bg-muted"
                    onClick={handleMarkAsRead}
                    title="Mark as read"
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive active:scale-95 active:bg-muted"
                  onClick={handleDelete}
                  title="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
